import posthog from "./posthog";

/**
 * Centralized analytics event helpers.
 * All PostHog captures go through here — keeps event names consistent
 * and makes it easy to find every instrumented touchpoint.
 *
 * Naming convention: <persona>_<action>_<object>
 */

// ─── Renter Funnel ────────────────────────────────────────────────────────────

export const track = {
  renter: {
    vehicleViewed: (props: { vehicleId: string; title: string; dailyPrice: number; city: string }) =>
      posthog.capture("renter_vehicle_viewed", props),

    bookingPageViewed: (props: { vehicleId: string; title: string; totalDays: number; totalPrice: number }) =>
      posthog.capture("renter_booking_page_viewed", props),

    pickupMethodSelected: (method: "pickup" | "delivery" | "chauffeur") =>
      posthog.capture("renter_pickup_method_selected", { method }),

    protectionPlanSelected: (plan: string) =>
      posthog.capture("renter_protection_plan_selected", { plan }),

    bookingSubmitted: (props: {
      vehicleId: string;
      totalDays: number;
      totalPrice: number;
      pickupType: string;
      protectionPlan: string;
      bookingMode: string;
    }) => posthog.capture("renter_booking_submitted", props),

    checkoutInitiated: (props: { vehicleId: string; totalPrice: number }) =>
      posthog.capture("renter_checkout_initiated", props),

    tripCheckInStarted: (tripId: string) =>
      posthog.capture("renter_trip_check_in_started", { tripId }),

    tripCheckOutStarted: (tripId: string) =>
      posthog.capture("renter_trip_check_out_started", { tripId }),

    reviewSubmitted: (tripId: string) =>
      posthog.capture("renter_review_submitted", { tripId }),
  },

  // ─── Host Funnel ────────────────────────────────────────────────────────────

  host: {
    revenueCalculatorUsed: (props: { estimatedMonthly: number }) =>
      posthog.capture("host_revenue_calculator_used", props),

    listingStarted: () =>
      posthog.capture("host_listing_started"),

    listingStepCompleted: (step: number, stepName: string) =>
      posthog.capture("host_listing_step_completed", { step, stepName }),

    listingPublished: (props: { vehicleId: string; dailyPrice: number }) =>
      posthog.capture("host_listing_published", props),

    stripeSetupViewed: () =>
      posthog.capture("host_stripe_setup_viewed"),
  },

  // ─── Fleet Manager Funnel ───────────────────────────────────────────────────

  fleet: {
    applicationStarted: () =>
      posthog.capture("fleet_application_started"),

    applicationSubmitted: (props: { city: string; state: string; vansCurrently: number; vansExpected: number }) =>
      posthog.capture("fleet_application_submitted", props),

    dashboardAccessed: () =>
      posthog.capture("fleet_dashboard_accessed"),

    setupStarted: () =>
      posthog.capture("fleet_setup_started"),
  },

  // ─── Buyer Funnel ───────────────────────────────────────────────────────────

  buyer: {
    pathSelected: (path: "personal" | "commercial") =>
      posthog.capture("buyer_path_selected", { path }),

    configuratorStepCompleted: (step: number, stepName: string) =>
      posthog.capture("buyer_configurator_step_completed", { step, stepName }),

    depositPageViewed: (buildId: string | null) =>
      posthog.capture("buyer_deposit_page_viewed", { buildId }),

    depositInitiated: (buildId: string | null) =>
      posthog.capture("buyer_deposit_initiated", { buildId }),

    depositCompleted: (buildId: string | null) =>
      posthog.capture("buyer_deposit_completed", { buildId }),
  },

  // ─── Gateway ────────────────────────────────────────────────────────────────

  gateway: {
    pathwayClicked: (pathway: "renter" | "host" | "fleet_manager" | "buyer", variant?: string) =>
      posthog.capture("gateway_pathway_clicked", { pathway, variant }),

    searchInitiated: (city: string) =>
      posthog.capture("gateway_search_initiated", { city }),

    experimentViewed: (experimentKey: string, variant: string) =>
      posthog.capture("$experiment_started", { experiment_key: experimentKey, variant_key: variant }),
  },

  // ─── Experiences Funnel ─────────────────────────────────────────────────────

  experiences: {
    serviceSelected: (serviceName: string) =>
      posthog.capture("experiences_service_selected", { serviceName }),

    bookingStepAdvanced: (step: number) =>
      posthog.capture("experiences_booking_step_advanced", { step }),

    bookingSubmitted: (props: { serviceName: string; totalPrice: number; bookingType: string }) =>
      posthog.capture("experiences_booking_submitted", props),

    bookingConfirmed: (props: { serviceName: string; totalPrice: number }) =>
      posthog.capture("experiences_booking_confirmed", props),
  },
};
